import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { saveUser } from '../customers';

const AVAILABLE_SERVICES = [
  'Cockroach Management',
  'Termite Control',
  'Bedbug Extermination',
  'Rodent Control',
  'Mosquito Control',
  'General Pest Control',
  'Fly Control',
  'Bird Netting',
];

export default function CreateUser() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    contact: '',
    email: '',
    services: ['Cockroach Management'],
    notes: '',
    billAmount: '',
    visitDate: new Date().toISOString().split('T')[0],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function handleChange(e) {
    let { name, value } = e.target;
    
    if (name === 'contact') {
      value = value.replace(/\D/g, '').substring(0, 10);
    }

    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  }

  function handleServiceToggle(service) {
    setForm((f) => {
      const exists = f.services.includes(service);
      const next = exists
        ? f.services.filter((s) => s !== service)
        : [...f.services, service];
      return { ...f, services: next.length ? next : [service] };
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cleanedContact = form.contact.replace(/\D/g, '');
    const newErrors = {};

    if (!form.firstName.trim()) {
      newErrors.firstName = 'First name is required.';
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = 'Last name is required.';
    }
    if (cleanedContact.length < 10) {
      newErrors.contact = 'Please enter a valid mobile number (at least 10 digits).';
    }
    if (!emailRegex.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (form.billAmount && Number(form.billAmount) < 0) {
      newErrors.billAmount = 'Bill amount cannot be negative.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      ...form,
      contact: cleanedContact
    };

    setErrors({});
    setSaving(true);
    try {
      await saveUser(payload);
      navigate('/admin/users');
    } catch (err) {
      setErrors({ global: err.message || 'Failed to save customer record. Make sure the backend server is running.' });
    } finally {
      setSaving(false);
    }
  }

  const inputClass =
    'w-full px-4 py-3 rounded border border-slate-300 bg-white text-xs md:text-sm text-slate-900 font-semibold placeholder-slate-400 outline-none focus:border-cta focus:bg-white focus:ring-4 focus:ring-cta/15 transition-all shadow-2xs';
  const errorInputClass =
    'w-full px-4 py-3 rounded border border-red-400 bg-red-50 text-xs md:text-sm text-slate-900 font-semibold placeholder-red-300 outline-none focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-500/15 transition-all shadow-2xs';

  return (
    <div className="space-y-6 w-full animate-rise relative z-10">
      {/* Title & Navigation Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <Link
            to="/admin/users"
            className="inline-flex items-center gap-1.5 text-xs font-bold text-accent-muted bg-white/80 backdrop-blur-md px-3.5 py-1.5 rounded border border-white/90 shadow-2xs hover:bg-white no-underline transition-all mb-3"
          >
            ← Back to Customer Directory
          </Link>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary m-0 tracking-tight">
            Record Service Visit
          </h1>
          <p className="text-xs md:text-sm text-txt-muted m-0 mt-1 font-medium">
            Create a new customer profile or record an audited treatment service visit log.
          </p>
        </div>
      </div>

      {errors.global && (
        <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded text-xs font-semibold shadow-xs flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
          {errors.global}
        </div>
      )}

      {/* Luxury Apple Glass Form Card */}
      <div className="bg-white/70 backdrop-blur-2xl border border-white/90 rounded p-6 md:p-6 shadow-lg relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-48 h-48 bg-accent/15 blur-3xl rounded pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-cta/10 blur-3xl rounded pointer-events-none" />

        <form onSubmit={handleSubmit} noValidate className="space-y-8 relative z-10">
          {/* Section 1: Customer Identity */}
          <div className="space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/80 pb-3">
              <span className="text-[11px] font-bold tracking-wider text-accent-muted bg-accent-soft px-3 py-1 rounded border border-accent/20 uppercase self-start inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>
                CUSTOMER IDENTITY DETAILS
              </span>
              <span className="text-xs text-txt-muted font-semibold">* Required Fields</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  First Name <span className="text-cta">*</span>
                </label>
                <input
                  className={errors.firstName ? errorInputClass : inputClass}
                  type="text"
                  name="firstName"
                  placeholder="Enter first name"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                />
                {errors.firstName && <p className="text-red-500 text-[10px] mt-1.5 font-bold flex items-center gap-1">{errors.firstName}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  Last Name <span className="text-cta">*</span>
                </label>
                <input
                  className={errors.lastName ? errorInputClass : inputClass}
                  type="text"
                  name="lastName"
                  placeholder="Enter last name"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                />
                {errors.lastName && <p className="text-red-500 text-[10px] mt-1.5 font-bold flex items-center gap-1">{errors.lastName}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  Contact Phone Hotline <span className="text-cta">*</span>
                </label>
                <input
                  className={errors.contact ? errorInputClass : inputClass}
                  type="tel"
                  name="contact"
                  placeholder="Enter 10-digit mobile number"
                  value={form.contact}
                  onChange={handleChange}
                  required
                />
                {errors.contact && <p className="text-red-500 text-[10px] mt-1.5 font-bold flex items-center gap-1">{errors.contact}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  Email Address <span className="text-cta">*</span>
                </label>
                <input
                  className={errors.email ? errorInputClass : inputClass}
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
                {errors.email && <p className="text-red-500 text-[10px] mt-1.5 font-bold flex items-center gap-1">{errors.email}</p>}
              </div>
            </div>
          </div>

          {/* Section 2: Treatment & Billing Specifications */}
          <div className="space-y-5 border-t border-white/80 pt-6">
            <div className="flex items-center justify-between border-b border-white/80 pb-3">
              <span className="text-[11px] font-bold tracking-wider text-cta bg-accent-soft px-3 py-1 rounded border border-accent/20 uppercase inline-flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17l-5.384 3.03.926-5.975L2.37 7.97l5.98-.87L11.42 2l2.63 5.1 5.98.87-4.592 4.255.926 5.975z" /></svg>
                TREATMENT &amp; BILLING LOG
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  Service Visit Date
                </label>
                <input
                  className={inputClass}
                  type="date"
                  name="visitDate"
                  value={form.visitDate}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-primary mb-1.5">
                  Bill Amount (₹ INR)
                </label>
                <input
                  className={errors.billAmount ? errorInputClass : inputClass}
                  type="number"
                  name="billAmount"
                  placeholder="Enter bill amount"
                  value={form.billAmount}
                  onChange={handleChange}
                />
                {errors.billAmount && <p className="text-red-500 text-[10px] mt-1.5 font-bold flex items-center gap-1">{errors.billAmount}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-primary mb-2">
                Services Performed (Select All Applicable)
              </label>
              <div className="flex flex-wrap gap-2.5">
                {AVAILABLE_SERVICES.map((service) => {
                  const selected = form.services.includes(service);
                  return (
                    <button
                      type="button"
                      key={service}
                      onClick={() => handleServiceToggle(service)}
                      className={`px-4 py-2.5 rounded text-xs font-semibold transition-all cursor-pointer border shadow-2xs ${selected
                        ? 'bg-cta text-white border-cta shadow-md scale-105'
                        : 'bg-white/80 text-primary border-accent/30 hover:bg-white hover:border-cta/40'
                        }`}
                    >
                      {selected ? '✓ ' : '+ '} {service}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-primary mb-1.5">
                Customer Remarks (This will be sent via SMS)
              </label>
              <textarea
                className={`${inputClass} min-h-[100px] resize-y`}
                name="notes"
                placeholder="e.g. Thank you for choosing Preventive Pest Terminators! Your 1-year warranty is now active."
                value={form.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 sm:gap-4 border-t border-white/80 pt-6">
            <Link
              to="/admin/users"
              className="px-6 py-3 rounded border border-border text-xs font-bold text-txt-muted hover:bg-white transition-all no-underline shadow-2xs text-center"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3.5 rounded bg-cta hover:bg-cta-hover text-white font-bold text-xs md:text-sm shadow-md hover:shadow-lg transition-all cursor-pointer border-0 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? 'Saving Record...' : 'Save Customer Record →'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
